/**
 * Stage a print-run matrix into the mixed print queue (QZ).
 * Uses team shipping-label attaches when carrier parts are requested.
 */

import { findGiftCardCandidates } from './giftCardCandidates';
import { createStandardPackingSlipMargins } from './packingSlipHtml';
import { buildWavePickListHtml } from './wavePickListHtml';
import { buildWavePickList, describeWaveStack, getWaveOrderKey } from './wavePickListModel';
import { loadQzPrintSettings } from '../services/qzPrintService';
import { stageTeamCarrierAttaches } from './stageTeamCarrierAttaches';
import {
  PRINT_RUN_PARTS,
  filterOrdersForPrintRunStep,
  sanitizePrintRunMatrix,
} from './printRunMatrixModel';

/**
 * @param {{
 *   matrix: object,
 *   orders: object[],
 *   attaches?: object[],
 *   folderLabel?: string,
 *   workingDeliveryDate?: string,
 *   plannerViews?: object[],
 *   printQueue: object,
 *   formatName?: Function,
 *   formatPack?: Function,
 * }} opts
 */
export async function stagePrintRunMatrix(opts) {
  const {
    matrix,
    orders = [],
    attaches = [],
    folderLabel = '',
    workingDeliveryDate = '',
    plannerViews = [],
    printQueue,
    formatName,
    formatPack,
  } = opts;
  if (!printQueue) throw new Error('Print queue is not available.');
  const safe = sanitizePrintRunMatrix(matrix);
  const qzSettings = loadQzPrintSettings();
  const giftCandidates = findGiftCardCandidates(orders);
  let stagedSteps = 0;
  let stagedOrders = 0;
  let stagedCarrierLabels = 0;
  const warnings = [];

  for (const step of safe.steps) {
    const scoped = filterOrdersForPrintRunStep(orders, step, { plannerViews });
    if (!scoped.length) {
      warnings.push(`Step “${step.label}” matched no orders.`);
      continue;
    }
    stagedSteps += 1;
    stagedOrders += scoped.length;
    const parts = step.parts || {};

    if (parts[PRINT_RUN_PARTS.pickList]) {
      const wave = buildWavePickList(scoped, {
        folderLabel: step.label || folderLabel,
        formatName,
        formatPack,
      });
      printQueue.enqueueWavePickList({
        html: buildWavePickListHtml(wave, {
          stack: describeWaveStack(wave, {
            includePickList: true,
            includeInscriptions: parts[PRINT_RUN_PARTS.inscription],
            includeCarrierLabels: parts[PRINT_RUN_PARTS.carrier],
            includeSeparators: parts[PRINT_RUN_PARTS.separator],
            format: parts[PRINT_RUN_PARTS.go4]
              ? 'go4'
              : parts[PRINT_RUN_PARTS.cheetah]
                ? 'cheetah'
                : 'standard',
          }),
        }),
        folderLabel: step.label || folderLabel,
        qzSettings,
        source: 'print-run-matrix',
      });
    }

    const wantSlip =
      parts[PRINT_RUN_PARTS.packingSlip] ||
      parts[PRINT_RUN_PARTS.cheetah] ||
      parts[PRINT_RUN_PARTS.go4];
    const giftCards = parts[PRINT_RUN_PARTS.inscription]
      ? giftCandidates
          .filter((candidate) => candidate.likely)
          .filter((candidate) =>
            scoped.some((order) => getWaveOrderKey(order) === candidate.orderKey),
          )
          .map((candidate) => ({
            orderKey: candidate.orderKey,
            order: candidate.order,
            message: candidate.message,
            sourceLabel: candidate.sourceLabel,
          }))
      : [];

    if (wantSlip) {
      const packingFormat = parts[PRINT_RUN_PARTS.go4]
        ? 'go4'
        : parts[PRINT_RUN_PARTS.cheetah]
          ? 'cheetah'
          : 'standard';
      printQueue.enqueuePackingSetup({
        orders: scoped,
        format: packingFormat,
        settings: {
          margins: createStandardPackingSlipMargins(),
          showPrices: false,
          showSignatureLines: true,
          includeDuplicate: packingFormat === 'standard',
        },
        qzSettings,
        giftCards,
        includeSeparators: parts[PRINT_RUN_PARTS.separator] === true,
        source: 'print-run-matrix',
      });
    } else if (giftCards.length) {
      printQueue.enqueueGiftCards({
        cards: giftCards,
        qzSettings,
        includeSeparators: parts[PRINT_RUN_PARTS.separator] === true,
        source: 'print-run-matrix',
      });
    }

    if (parts[PRINT_RUN_PARTS.carrier]) {
      const carrierResult = await stageTeamCarrierAttaches({
        attaches,
        orders: scoped,
        workingDeliveryDate,
        enqueueCarrierLabel: printQueue.enqueueCarrierLabel,
      });
      stagedCarrierLabels += carrierResult.stagedLabels;
      if (!carrierResult.stagedLabels) {
        warnings.push(
          `Step “${step.label}” asked for shipping labels but none were attached for its orders.`,
        );
      } else if (carrierResult.missingOrderKeys.length) {
        warnings.push(
          `Step “${step.label}”: ${carrierResult.missingOrderKeys.length} order(s) have no attached carrier page.`,
        );
      }
    }
  }

  return {
    stagedSteps,
    stagedOrders,
    stagedCarrierLabels,
    stepCount: safe.steps.length,
    warnings,
  };
}
